// Phase 43 — <CanvasSectionNav /> (Plan 43-05 Task 1, CANVAS-01).
// Sticky 200px left rail on desktop (≥1280px); top sticky <Select> dropdown
// on tablet/phone per UI-SPEC §"Layout Contract" (rev 2).
//
// Visual vocabulary mirrors AddBoatWizardStepper (200px column, icon + label
// row), but this component is scroll-spy-driven, NOT step-based — clicking
// an item invokes `onJumpTo(id)` which is implemented by `useCanvasNav` and
// resolves to a smooth scrollIntoView + 600ms IO suppression guard.
//
// Typography lock (UI-SPEC rev 2): 13px (`text-[0.8125rem]`) at weight 400
// inactive / 600 active. The 12px size and weight-500 utility class names
// are disallowed by UI-SPEC. Active item also gets `text-primary` AND a 2px
// left-border (--primary) — three combined hierarchy signals per typography
// scoping table.

import type { ComponentType } from 'react';
import {
  FileText,
  Cog,
  Anchor,
  Truck,
  Package,
  DollarSign,
  Globe,
  CheckCircle2,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@marine/ui/select';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

// Section IDs — strawman from Plan 43-05 interfaces. Each id matches a
// `<section id="...">` rendered by <CanvasFormSections /> (Task 2).
// Phase 45.5.2 (CANVAS-10) — the 'photos' section was removed when the separate
// Photos upload section was collapsed into the hero's unified dropzone, so its
// scroll-spy entry is dropped here too (no dangling #photos anchor).
export const CANVAS_SECTION_IDS = [
  'identity',
  'specs',
  'engines',
  'trailer',
  'equipment',
  'pricing',
  'listing',
  'review',
] as const;
export type CanvasSectionId = (typeof CANVAS_SECTION_IDS)[number];

interface SectionDescriptor {
  id: CanvasSectionId;
  label: string;
  Icon: ComponentType<{ className?: string }>;
}

const SECTIONS: SectionDescriptor[] = [
  { id: 'identity', label: 'Identity', Icon: FileText },
  { id: 'specs', label: 'Specs', Icon: Cog },
  { id: 'engines', label: 'Engines', Icon: Anchor },
  { id: 'trailer', label: 'Trailer', Icon: Truck },
  { id: 'equipment', label: 'Equipment', Icon: Package },
  { id: 'pricing', label: 'Pricing', Icon: DollarSign },
  { id: 'listing', label: 'Listing', Icon: Globe },
  { id: 'review', label: 'Review', Icon: CheckCircle2 },
];

interface CanvasSectionNavProps {
  activeSection: string;
  onJumpTo: (id: string) => void;
}

export function CanvasSectionNav({
  activeSection,
  onJumpTo,
}: CanvasSectionNavProps) {
  const isDesktop = useMediaQuery('(min-width: 1280px)');

  if (!isDesktop) {
    return (
      <div className="sticky top-0 z-10 border-b bg-card px-4 py-2">
        <Select value={activeSection} onValueChange={onJumpTo}>
          <SelectTrigger
            aria-label="Listing sections"
            className="w-full text-[0.8125rem] font-normal"
          >
            <SelectValue placeholder="Jump to section" />
          </SelectTrigger>
          <SelectContent>
            {SECTIONS.map(({ id, label }) => (
              <SelectItem
                key={id}
                value={id}
                className="text-[0.8125rem] font-normal"
              >
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <nav
      aria-label="Listing sections"
      className="sticky top-0 border-e bg-card py-4"
    >
      <ol className="flex flex-col">
        {SECTIONS.map(({ id, label, Icon }) => {
          const isActive = activeSection === id;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                aria-current={isActive ? 'location' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  onJumpTo(id);
                }}
                className={cn(
                  'flex items-center gap-3 rounded-none px-4 py-2.5',
                  // 13px label per UI-SPEC §Typography
                  'text-[0.8125rem]',
                  isActive
                    ? 'font-semibold text-primary border-l-2 border-primary'
                    : 'font-normal text-muted-foreground hover:bg-accent/50 border-l-2 border-transparent',
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
